// src/services/secureApi.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const createSecureApiClient = () => {
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
  });

  // Intercepteur pour ajouter le token
  apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Intercepteur pour gérer les erreurs d'authentification
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expiré ou invalide
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('businessData');
        window.location.href = '/login?session=expired';
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

// Fonctions sécurisées pour les contacts
export const secureContactApi = {
  // Récupérer la liste des contacts
  fetchContactsList: async () => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.get('/contacts');
      return response.data;
    } catch (error) {
      console.error('Erreur fetchContactsList:', error);
      throw error;
    }
  },

  // Ajouter un contact
  addContact: async (contactData) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.post('/contacts', contactData);
      return response.data;
    } catch (error) {
      console.error('Erreur addContact:', error);
      throw error;
    }
  },

  // Supprimer un contact
  deleteContact: async (contactId) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.delete(`/contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur deleteContact:', error);
      throw error;
    }
  },

  // Mettre à jour un contact
  updateContact: async (contactId, contactData) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.put(`/contacts/${contactId}`, contactData);
      return response.data;
    } catch (error) {
      console.error('Erreur updateContact:', error);
      throw error;
    }
  },

  // Récupérer les groupes
  fetchContactGroups: async () => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.get('/contact-groups');
      return response.data;
    } catch (error) {
      console.error('Erreur fetchContactGroups:', error);
      return [];
    }
  }
};

// Fonctions sécurisées pour la gestion des groupes
export const secureGroupApi = {
  // Récupérer tous les groupes
  fetchGroups: async () => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.get('/contact-groups');
      return response.data;
    } catch (error) {
      console.error('Erreur fetchGroups:', error);
      throw error;
    }
  },

  // Récupérer les détails d'un groupe
  fetchGroupDetail: async (groupId) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.get(`/contact-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur fetchGroupDetail:', error);
      throw error;
    }
  },

  // Créer un groupe
  createGroup: async (groupData) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.post('/contact-groups', {
        name: groupData.name,
      });
      return response.data;
    } catch (error) {
      console.error('Erreur createGroup:', error);
      throw error;
    }
  },

  // Mettre à jour un groupe
  updateGroup: async (groupId, groupData) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.put(`/contact-groups/${groupId}`, {
        name: groupData.name,
      });
      return response.data;
    } catch (error) {
      console.error('Erreur updateGroup:', error);
      throw error;
    }
  },

  // Supprimer un groupe
  deleteGroup: async (groupId) => {
    try {
      const apiClient = createSecureApiClient();
      const response = await apiClient.delete(`/contact-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur deleteGroup:', error);
      throw error;
    }
  }
};

// Configuration des couleurs pour les groupes (commun)
export const groupeCouleurs = [
  { value: "primary", label: "Bleu" },
  { value: "success", label: "Vert" },
  { value: "warning", label: "Orange" },
  { value: "danger", label: "Rouge" },
  { value: "info", label: "Cyan" },
  { value: "secondary", label: "Gris" },
  { value: "dark", label: "Noir" },
  { value: "light", label: "Clair", textColor: "dark" }
];